////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.1                                                            //
//  Summary: Typography wrapper for long-form content                       //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import type { ReactNode } from 'react';

export interface ProseProps {
  children: ReactNode;
  className?: string | undefined;
}

/** Tag-level typography, shared VERBATIM with the admin editor's content area
 * (components/admin/Editor.tsx) so editing is visually identical to the public
 * render — the 14/08/2026 editor bug ("H2 didn't change in the editor") was
 * exactly this styling missing on the editor side. Tailwind preflight strips
 * list bullets and heading scale, so every long-form tag is restated here. */
export const PROSE_TYPOGRAPHY = [
  '[&_h1]:text-3xl [&_h1]:font-semibold [&_h2]:text-2xl [&_h2]:font-semibold',
  '[&_h3]:text-xl [&_h3]:font-semibold [&_h4]:text-lg [&_h4]:font-semibold',
  '[&_a]:underline',
  '[&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1',
  '[&_code]:rounded [&_code]:bg-muted/10 [&_code]:px-1 [&_code]:font-mono [&_code]:text-sm',
  '[&_pre]:overflow-x-auto [&_pre]:rounded [&_pre]:bg-muted/10 [&_pre]:p-4 [&_pre_code]:bg-transparent [&_pre_code]:p-0',
  '[&_table]:w-full [&_table]:border-collapse [&_th]:border [&_th]:border-muted/40 [&_th]:bg-muted/10 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-semibold',
  '[&_td]:border [&_td]:border-muted/40 [&_td]:px-2 [&_td]:py-1 [&_td]:align-top',
].join(' ');

/** Applies the article/page typography scale to whatever is rendered inside it —
 * `<Markdown>` output, or hand-written JSX on a static page. */
export function Prose ({ children, className = '' }: ProseProps) {
  return (
    <div
      className={`max-w-3xl space-y-6 text-base leading-relaxed text-ink ${PROSE_TYPOGRAPHY} ${className}`}
    >
      {children}
    </div>
  );
}
